import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateProject } from '../hooks/useCreateProject';
import { CreateProjectForm } from '../components/CreateProjectForm';
import { useCreateProjectValidation, CreateProjectFormData } from '../hooks/useCreateProjectValidation';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function CreateProject() {
  const [formData, setFormData] = useState<CreateProjectFormData>({
    name: '',
    address: '',
    startDate: '',
    endDate: '',
    paymentMethod: '',
    hasConstructionTracking: false,
    hasAdhocTracking: false,
    internalNotes: '',
  });

  const navigate = useNavigate();
  const { createProject, loading, error } = useCreateProject();
  const { errors, validate, clearErrors, setError: setValidationError } = useCreateProjectValidation();

  const handleFormChange = (field: keyof CreateProjectFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific errors when user starts typing
    if (errors[field as keyof typeof errors]) {
      clearErrors();
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearErrors();

    if (!validate(formData)) {
      return;
    }

    try {
      // Clean the form data - remove empty strings for optional fields
      const cleanData = {
        name: formData.name,
        paymentMethod: formData.paymentMethod,
        ...(formData.address && { address: formData.address }),
        ...(formData.startDate && { startDate: formData.startDate }),
        ...(formData.endDate && { endDate: formData.endDate }),
        ...(formData.hasConstructionTracking !== undefined && { hasConstructionTracking: formData.hasConstructionTracking }),
        ...(formData.hasAdhocTracking !== undefined && { hasAdhocTracking: formData.hasAdhocTracking }),
        ...(formData.internalNotes && { internalNotes: formData.internalNotes }),
      };

      const project = await createProject(cleanData);
      navigate(`/projects/${project.id}`);
    } catch (err) {
      console.error(err);
      setValidationError('general', 'Erro ao criar projeto');
    }
  };

  return (
    <div className="min-h-screen bg-primary-50 dark:bg-primary-950 py-24">
      <div className="container-elegant max-w-3xl">
        <h1 className="text-3xl font-light tracking-tight text-primary-950 dark:text-white mb-12">
          NOVO PROJETO
        </h1>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        <CreateProjectForm
          formData={formData}
          onChange={handleFormChange}
          onSubmit={handleSubmit}
          errors={errors}
          loading={loading}
        />
      </div>
    </div>
  );
}